'use client';

import { Price as SparkyPrice, TextPassage } from '@petsmart-ui/sparky';

import { useDecisions, useOptimizely } from '@libs/common-next';
import { AutoshipPurchaseOptions } from '@libs/shared-ui';

import { isProductAvailableForAutoship, useProduct } from '@/entities/product';
import { register, usePurchaseFormWatch } from '@/entities/purchase-form';
import { useAutoshipCustomerStatus } from '@/features/autoship-customer-status';
import { useAutoshipSubscription } from '@/widgets/product-details/subscribed-with-autoship';

import { getAutoshipDiscountMessages } from '../../lib';
import { useOneTimePrice } from '../../model';
import { FrequencyOptions } from '../FrequencyOptions';
import { AutoshipInfoDrawer } from './AutoshipInfoDrawer';
import { FrequencyCalculatorCta } from './FrequencyCalculatorCta';

export const PurchaseOptions = () => {
    const { product } = useProduct();
    const autoshipFlag = useDecisions('ff_autoship');
    const isSubscribedIndicatorEnabled = useDecisions('x_as_sub_ind');
    const isPdpDynPxEnabled = useDecisions('x_pdp_dyn_px');
    const isFrequencyCalculatorEnabled = useDecisions('pdp_as_fr_calc');
    // Align with useGetDefaultValues: usePurchaseFormWatch returns undefined during SSR (see IS_SSR in usePurchaseFormWatch).
    const selectedPurchaseOption = usePurchaseFormWatch('purchaseOption') ?? 'one-time';

    // TODO: Replace it with useEffect, remove handlers
    const { context } = useOptimizely();
    const handleOptiEvent = (e: React.KeyboardEvent | React.MouseEvent) => {
        const input = (e.currentTarget as HTMLInputElement)?.value;
        if (input) {
            context?.trackEvent(`${input}-purchase-click`);
        }
    };

    const { isReady, isLoading, isFetching, isFirstAutoshipEligible } = useAutoshipCustomerStatus();
    const {
        isSubscribed: isSubscribedToItem,
        isReady: isSubscriptionReady,
        isError: isSubscriptionError,
    } = useAutoshipSubscription();

    const oneTimePrice = useOneTimePrice();

    if (!product || !isProductAvailableForAutoship(product)) return null;

    const shouldShowSkeleton =
        isLoading || (isFetching && isFirstAutoshipEligible) || (!isSubscriptionReady && !isSubscriptionError);

    const autoshipDiscountMessages = getAutoshipDiscountMessages({
        autoshipFlag,
        isFirstAutoshipEligible,
        discountDisplay: product?.autoship.discountDisplay,
    });
    const isSubscribedIndicatorShown = isSubscribedIndicatorEnabled && isSubscribedToItem;
    const messages = isSubscribedIndicatorShown
        ? {
              autoshipDiscountHeader: autoshipDiscountMessages.autoshipDiscountHeader,
              autoshipDiscountSubtext: "You're already subscribed to this item.",
          }
        : autoshipDiscountMessages;

    return (
        <AutoshipPurchaseOptions
            selected={selectedPurchaseOption}
            inputProps={{ ...register('purchaseOption'), onKeyDown: handleOptiEvent, onClick: handleOptiEvent }}
            isAutoshipDisabled={!isReady}
            isLoading={shouldShowSkeleton}
            messages={messages}
            footnote={autoshipFlag === 'precrawl' ? 'Only available with Ship to me.' : undefined}
            oneTimePriceSlot={
                isPdpDynPxEnabled &&
                oneTimePrice && (
                    <SparkyPrice
                        className="purchase-options__option--price"
                        data-testid="pdp-one-time-price"
                        values={oneTimePrice.values}
                        variant={oneTimePrice.variant}
                    />
                )
            }
            infoSlot={
                <AutoshipInfoDrawer
                    discountDisplay={product?.autoship.discountDisplay}
                    isFirstAutoshipEligible={isFirstAutoshipEligible}
                />
            }
        >
            <FrequencyOptions />
            {isFrequencyCalculatorEnabled && product.isFrequencyCalculatorEligible && <FrequencyCalculatorCta />}
            {autoshipFlag === 'precrawl' ? null : (
                <TextPassage className="purchase-options__autoship-text" size="sm">
                    Choose how you want to receive your first order below.
                </TextPassage>
            )}
        </AutoshipPurchaseOptions>
    );
};
